import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Child, Item } from "@eshop/types/catalogue.types.ts";
import { conns } from "@cfg/axiosConfig.tsx";
import { logError, logInfo } from "@tools/LogsHandler.tsx";
import { isAxiosError } from 'axios';
import { RootState } from "@store/store.tsx";
import { Category } from "@eshop/types/categories.types.ts";
import { filterItem, filterItems } from "@eshop/components/catalogue/Cataloguefunctions.tsx";
import { selectErpEnabled } from '@store/slices/settingsSlice.tsx';
import { findCategoryById, upsertCategoryNode } from '@eshop/components/categories/categoriesUtils.tsx';

//    // Para definir que solo persistan valores específicos
// import storage from 'redux-persist/lib/storage' // defaults to localStorage for web
// const cataloguePersistConfig = {
//     key: 'catalogue',
//     storage: storage,
//     whitelist: ['products', 'categories']  // Only persist 'products' and 'categories'
// };

const idsMatch = (
  a: string | number | null | undefined,
  b: string | number | null | undefined,
): boolean => {
  if (a === undefined || a === null || b === undefined || b === null) {
    return false;
  }
  return String(a) === String(b);
};

// Definimos la estructura del error
interface ErrorPayload {
  [key: string]: string[];
}

export interface ErrorState {
  message: string;
  errors?: ErrorPayload;
}

export interface HorariosDisponiblesType {
  [day: number]: [number, number][];
}

interface CfgCatalogueData {
  initialized: boolean;
  urls: {
    cart: string;
    checkout: string;
    home: string;
    catalogue: string;
    web?: string;
    contact: string;
  }
  web_disponible: boolean;
  dias_entrega: number;
  horarios: HorariosDisponiblesType;
  upc_as_main: boolean;
  user_is_authenticated: boolean;
  user_is_staff: boolean;
  item_aspect_card: string;
  wsp_numbers?: Record<string, string>;
  float_btn_msj?: boolean;
  float_btn_msj_txt?: string;
}

interface CatalogueState {
  isItemLoading: boolean;
  isCategoriesLoading: boolean;
  hasLoaded: boolean;
  error: string | ErrorState | null;
  initialized: boolean;

  data: {
    categories: Category[];
    categorySelectedId: number | null;
    selectedCategory?: Category;
    items: Item[];
    products: Item[];
    services: Item[];
    page: number;
    orderBy: string | null;
    orderByOptions: OrderByOption[];
    searchPrompt: string | null;
    hasMore: boolean;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    filters: { [key: string]: any }
  },
  cfg: CfgCatalogueData,
}

interface OrderByOption {
  value: string,
  label: string,
}

const DASHBOARD_DEFAULT_ORDER_BY = 'newest';
const DASHBOARD_DEFAULT_FILTERS: Record<string, unknown> = {
  active: true,
};

const initialState: CatalogueState = {
  isItemLoading: false,
  isCategoriesLoading: false,
  hasLoaded: false,
  error: null,
  initialized: false,

  data: {
    categories: [],
    categorySelectedId: null,
    items: [],
    products: [],
    services: [],
    page: 1,
    searchPrompt: null,
    orderBy: null,
    orderByOptions: [
      { value: 'relevancy', label: 'Más relevantes' },
      { value: 'rating', label: 'Mejor Valorados' },
      { value: 'newest', label: 'Más Recientes' },
      { value: 'price-asc', label: 'Menor Precio' },
      { value: 'price-desc', label: 'Mayor Precio' },
      { value: 'title-asc', label: 'De A a Z' },
      { value: 'title-desc', label: 'De Z a A' },
    ],
    hasMore: true,
    filters: {},
  },
  cfg: {
    initialized: false,
    urls: { cart: '', checkout: '', catalogue: '', home: '', contact: '' },
    web_disponible: false,
    dias_entrega: 0,
    horarios: {},
    upc_as_main: false,
    user_is_authenticated: false,
    user_is_staff: false,
    item_aspect_card: 'aspect_1-1',
  }
};

interface PromotionPriceHydrationMeta {
  pending?: boolean;
  queued_count?: number;
  missing_count?: number;
  retry_after_ms?: number;
}

interface CatalogueDataListResponse {
  results: Item[];
  next: string;
  promotion_price_hydration?: PromotionPriceHydrationMeta | null;
}

export interface PromotionHydrationItemPatch {
  id: number;
  price?: Item['price'];
  promotions?: Item['promotions'];
  promotion_labels?: string[];
}

interface PromotionHydrationRefreshResponse {
  results: PromotionHydrationItemPatch[];
  promotion_price_hydration?: PromotionPriceHydrationMeta | null;
}

interface dataItemsRequest { page: number | null, order_by: string | null, category?: number | null, search_prompt?: string | null }

export interface BulkToggleSummary {
  updated_ids: number[];
  skipped_ids: number[];
  unchanged_ids: number[];
}

export interface BulkTogglePayload {
  is_active?: boolean;
  is_visible_web?: boolean;
}
export const DASHBOARD_ITEMS_URL = 'eshop:dashboard:item:edit';

let promotionHydrationRefetchAttempts = 0;
let promotionHydrationRefetchTimer: ReturnType<typeof setTimeout> | null = null;

const buildHydrationViewKey = (state: RootState, urlName: string): string => JSON.stringify({
  urlName,
  orderBy: state.eshop.data.orderBy,
  categorySelectedId: state.eshop.data.categorySelectedId,
  searchPrompt: state.eshop.data.searchPrompt,
  filters: state.eshop.data.filters,
});

const clearPromotionHydrationPolling = () => {
  promotionHydrationRefetchAttempts = 0;
  if (promotionHydrationRefetchTimer) {
    clearTimeout(promotionHydrationRefetchTimer);
    promotionHydrationRefetchTimer = null;
  }
};

const schedulePromotionHydrationPolling = (
  dispatch: (action: unknown) => unknown,
  args: { urlName?: string },
  viewKey: string,
  retryAfterMs: number,
) => {
  if (promotionHydrationRefetchAttempts >= 3) {
    return;
  }
  promotionHydrationRefetchAttempts += 1;
  if (promotionHydrationRefetchTimer) {
    clearTimeout(promotionHydrationRefetchTimer);
  }
  promotionHydrationRefetchTimer = setTimeout(() => {
    void dispatch(pollPromotionHydrationAsync({
      ...args,
      viewKey,
    }));
  }, retryAfterMs);
};

const patchPromotionFields = <T extends Item | Child>(
  item: T,
  patch: PromotionHydrationItemPatch,
): T => ({
  ...item,
  ...(patch.price !== undefined ? { price: patch.price } : {}),
  ...(patch.promotions !== undefined ? { promotions: patch.promotions } : {}),
  ...(patch.promotion_labels !== undefined ? { promotion_labels: patch.promotion_labels } : {}),
} as T);

export const mergePromotionHydrationPatchesForItems = (
  items: Item[],
  patches: PromotionHydrationItemPatch[],
): Item[] => {
  if (!patches.length) {
    return items;
  }
  const patchById = new Map(patches.map((patch) => [String(patch.id), patch]));
  let changed = false;
  const merged = items.map((item) => {
    const patch = patchById.get(String(item.id));
    let childChanged = false;
    const patchedChildren = item.children?.map((child) => {
      const childPatch = patchById.get(String(child.id));
      if (!childPatch) {
        return child;
      }
      childChanged = true;
      changed = true;
      return patchPromotionFields(child, childPatch);
    });
    const itemWithChildren = childChanged && patchedChildren
      ? { ...item, children: patchedChildren }
      : item;

    if (patch) {
      changed = true;
      return patchPromotionFields(itemWithChildren, patch);
    }

    if (childChanged) {
      return itemWithChildren;
    }
    return item;
  });
  return changed ? merged : items;
};

const collectVisibleItemIds = (items: Item[]): number[] => items.flatMap((item) => {
  const ids: number[] = [];
  if (typeof item.id === 'number') {
    ids.push(item.id);
  }
  item.children?.forEach((child) => {
    if (typeof child.id === 'number') {
      ids.push(child.id);
    }
  });
  return ids;
});

// Async thunks
export const fetchItemsAsync = createAsyncThunk(
  'catalogue/fetchItemsAsync',
  async (args: { urlName?: string }, { getState, rejectWithValue, dispatch }) => {
    const state = getState() as RootState;
    const orderBy = state.eshop.data.orderBy;
    const categorySelectedId = state.eshop.data.categorySelectedId;
    const searchPrompt = state.eshop.data.searchPrompt;

    // const urlName = args.urlName || 'eshop:catalogue:get_datalist';
    const urlName = args.urlName || 'eshop:items';

    const filters = state.eshop.data.filters;  // Aquí se almacenan los valores de los filtros

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data: any = {
      page: 1,
      order_by: orderBy,
      ...filters,
      _: new Date().getTime() // Add a timestamp to prevent caching
    };

    if (categorySelectedId) {
      data.category = categorySelectedId;
    }
    if (searchPrompt) {
      data.search_prompt = searchPrompt;
    }

    try {
      clearPromotionHydrationPolling();
      const itemsResponse = await conns<CatalogueDataListResponse>(urlName).get(data);
      const hydration = itemsResponse.data.promotion_price_hydration;
      if (hydration?.pending) {
        const retryAfterMs = Math.max(250, Number(hydration.retry_after_ms ?? 1500));
        schedulePromotionHydrationPolling(
          dispatch,
          args,
          buildHydrationViewKey(state, urlName),
          retryAfterMs,
        );
      } else if (!hydration?.pending) {
        clearPromotionHydrationPolling();
      }
      return itemsResponse.data;
    } catch (error) {
      return rejectWithValue(`Error fetching products: ${error}`);
    }
  }
);

export const pollPromotionHydrationAsync = createAsyncThunk(
  'catalogue/pollPromotionHydrationAsync',
  async (
    args: { urlName?: string; viewKey: string },
    { getState, rejectWithValue, dispatch },
  ) => {
    const state = getState() as RootState;
    const urlName = args.urlName || 'eshop:items';
    if (buildHydrationViewKey(state, urlName) !== args.viewKey) {
      return { results: [], promotion_price_hydration: null };
    }

    const itemIds = collectVisibleItemIds(state.eshop.data.items);
    if (!itemIds.length) {
      clearPromotionHydrationPolling();
      return { results: [], promotion_price_hydration: null };
    }

    try {
      const response = await conns<PromotionHydrationRefreshResponse>(
        'eshop:catalogue:promotion_prices_refresh',
      ).get({ item_ids: itemIds.join(',') });
      const hydration = response.data.promotion_price_hydration;
      if (hydration?.pending) {
        schedulePromotionHydrationPolling(
          dispatch,
          { urlName },
          args.viewKey,
          Math.max(250, Number(hydration.retry_after_ms ?? 1500)),
        );
      } else {
        clearPromotionHydrationPolling();
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(`Error polling promotion prices: ${error}`);
    }
  },
);

export const fetchAddItemsFromPageAsync = createAsyncThunk(
  'catalogue/fetchAddItemsFromPageAsync',
  async (args: { urlName?: string, page: number }, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const orderBy = state.eshop.data.orderBy;
    const categorySelectedId = state.eshop.data.categorySelectedId;
    const searchPrompt = state.eshop.data.searchPrompt;

    const urlName = args.urlName || 'eshop:items';

    const filters = state.eshop.data.filters;  // Aquí se almacenan los valores de los filtros

    const data: dataItemsRequest = { page: args.page, order_by: orderBy, ...filters }
    if (categorySelectedId) {
      data.category = categorySelectedId;
    }
    if (searchPrompt) {
      data.search_prompt = searchPrompt;
    }

    try {
      const itemsResponse = await conns<CatalogueDataListResponse>(urlName).get(data);
      return { page: args.page, itemsToAdd: itemsResponse.data.results as Item[], next: itemsResponse.data.next };
    } catch (error) {
      return rejectWithValue(`Error fetching products: ${error}`);
    }
  }
);

export const fetchCategoriesAsync = createAsyncThunk<
  Category[],
  { node_id?: number; depth?: number } | undefined
>(
  'catalogue/fetchCategories',
  async (params, { rejectWithValue }) => {
    try {
      const categoriesResponse = await conns<Category[]>(
        'eshop:category:tree'
      ).get(params);
      return categoriesResponse.data;
    } catch (error) {
      logError('fetchCategoriesAsync Err: ', error);
      return rejectWithValue('Unable to fetch categories');
    }
  }
);


export const fetchFallbackImagesAsync = createAsyncThunk<
  { category_id: number; image: Record<string, any> }[],
  { depth?: number } | undefined
>(
  'catalogue/fetchFallbackImages',
  async (params, { rejectWithValue }) => {
    try {
      const depth = params?.depth ?? 0;
      const response = await conns<{ category_id: number; image: Record<string, any> }[]>(
        'eshop:category:fallback_images'
      ).post(null, { params: { depth } });
      return response.data;
    } catch (error) {
      logError('fetchFallbackImagesAsync Err: ', error);
      return rejectWithValue('Unable to fetch fallback images');
    }
  }
);


export const fetchCfgCatalogueDataAsync = createAsyncThunk(
  'catalogue/fetchCfgCatalogueDataAsync',
  async (_, { getState, rejectWithValue }) => {
    const state = getState() as RootState;
    const initialized = state.eshop.cfg.initialized;
    if (!initialized) {
      try {
        const cfgCatalogueDataResponse = await conns<CfgCatalogueData>('eshop:catalogue:get_catalogue_cfg_data').get();
        return cfgCatalogueDataResponse.data;
      } catch (error) {
        return rejectWithValue(`Error fetching cfg data catalogue: ${error}`);
      }
    }
  }
);


export const fetchCatalogueItemUpdateAsync = createAsyncThunk(
  'catalogue/fetchCatalogueProductUpdateAsync',
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  async (args: { id: number; data: { [key: string]: any }; isFullUpdate: boolean }, { rejectWithValue }) => {
    try {
      const method = args.isFullUpdate ? 'put' : 'patch';
      const endpointKey = 'eshop:dashboard:item:edit';  // args.isFullUpdate ? 'eshop:dashboard:item:edit' : 'eshop:items';
      logInfo(` ---------------- fetchCatalogueProductUpdateAsync (${method}) args:`, args);
      const payloadLog = args.data instanceof FormData ? Object.fromEntries(args.data.entries()) : args.data;
      logInfo(`fetchCatalogueProductUpdateAsync (${method}) DataToSend:`, payloadLog);

      const client = args.id
        ? conns<Item>(endpointKey, [args.id])
        : conns<Item>('eshop:dashboard:item:add');

      const catalogueItemDataResponse = args.id
        ? await client[method](args.data)
        : await client.post(args.data);
      logInfo(`catalogueProductDataResponse.data (${method}):`, catalogueItemDataResponse.data);
      return { id: args.id, data: catalogueItemDataResponse.data };
    } catch (error: unknown) {
      logError('*** CATCH ERROR in fetchCatalogueProductUpdateAsync ***', error); // Catch-all log

      if (isAxiosError(error) && error.response) {
        logError('*** AXIOS RESPONSE ERROR ***', {
          status: error.response.status,
          data: error.response.data,
          headers: error.response.headers,
        });

        if (error.response.status === 400 || error.response.status === 422) {
          // Devolver los errores de validación
          return rejectWithValue(error.response.data);
        }
        if (error.response.status === 403) {
          const detail = (error.response.data as { detail?: unknown })?.detail;
          if (detail && typeof detail === 'object') {
            return rejectWithValue(detail);
          }
          if (typeof detail === 'string') {
            return rejectWithValue({ message: detail });
          }
          return rejectWithValue({ code: 'service_creation_forbidden_without_erp' });
        }
      }

      if (error instanceof Error) {
        return rejectWithValue({ message: `Error al cargar los datos de producto: ${error.message}` });
      }

      // Si error no es una instancia de Error, manejamos el caso genérico
      return rejectWithValue({ message: 'Error desconocido al cargar los datos de producto' });
    }
  }
);

/**
 * Bulk toggle de is_visible_web para el queryset filtrado en el backend
 */
export const fetchBulkToggleVisibilityAsync = createAsyncThunk<
  { summary: BulkToggleSummary; toggles: BulkTogglePayload },
  BulkTogglePayload
>(
  'catalogue/fetchBulkToggleVisibility',
  async (
    payload,
    { getState, rejectWithValue }
  ) => {
    const state = getState() as RootState;
    const { filters, orderBy, searchPrompt } = state.eshop.data;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: Record<string, any> = { ...filters };
    if (orderBy) params.order_by = orderBy;
    if (searchPrompt) params.search_prompt = searchPrompt;

    try {
      const bulkToggleDataResponse = await conns<BulkToggleSummary>('eshop:items_bulk_toggle').post(
        payload,
        { params },
      );

      logInfo(
        'fetchBulkToggleVisibilityAsync - bulkToggleDataResponse.data: ',
        bulkToggleDataResponse.data,
      );
      return { summary: bulkToggleDataResponse.data, toggles: payload };
    } catch (err) {
      logError('fetchBulkToggleVisibilityAsync error', err);
      return rejectWithValue(err);
    }
  }
);

export const eshopSlice = createSlice({
  name: 'catalogue',
  initialState,
  reducers: {
    setCategorySelected(state, action: PayloadAction<number | null>) {
      state.data.categorySelectedId = action.payload;

      if (action.payload) {
        // Intentar obtener la categoría inmediatamente
        state.data.selectedCategory = findCategoryById(state.data.categories, action.payload);
      } else {
        state.data.selectedCategory = undefined;
      }

      logInfo('state.data.categorySelectedId: ', state.data.categorySelectedId);
    },
    setItems(state, action: PayloadAction<Item[]>) {
      state.data.items = action.payload;
      state.data.products = action.payload.filter(i => i.item_type === 'product');
      state.data.services = action.payload.filter(i => i.item_type === 'service');
      state.hasLoaded = true;
    },
    setInitialized(state, action: PayloadAction<boolean>) {
      state.initialized = action.payload;
    },
    setOrderby(state, action: PayloadAction<string>) {
      state.data.orderBy = action.payload;
    },
    setSearchPrompt(state, action: PayloadAction<string | null>) {
      state.data.searchPrompt = action.payload;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setFilters(state, action: PayloadAction<Record<string, any>>) {
      const nextFilters = { ...state.data.filters };
      Object.entries(action.payload).forEach(([key, value]) => {
        if (value === undefined) {
          delete nextFilters[key];
        } else {
          nextFilters[key] = value;
        }
      });
      state.data.filters = nextFilters;
    },
    resetDashboardItemsQueryState(state) {
      state.data.orderBy = DASHBOARD_DEFAULT_ORDER_BY;
      state.data.searchPrompt = null;
      state.data.filters = { ...DASHBOARD_DEFAULT_FILTERS };
      state.data.categorySelectedId = null;
      state.data.selectedCategory = undefined;
      state.data.page = 1;
      state.data.hasMore = true;
      state.hasLoaded = false;
    },
    setAspectRadio(state, action: PayloadAction<string>) {
      state.cfg.item_aspect_card = action.payload;
    },
    removeItem: (state, action: PayloadAction<string | number>) => {
      logInfo('Se filtra el producto borrado');
      state.data.items = state.data.items.filter(item => item.id !== action.payload);
      state.data.products = state.data.items.filter(i => i.item_type === 'product');
      state.data.services = state.data.items.filter(i => i.item_type === 'service');
    },
    upsertCategory(state, action: PayloadAction<Category>) {
      state.data.categories = upsertCategoryNode(state.data.categories, action.payload);
      if (state.data.categorySelectedId) {
        state.data.selectedCategory = findCategoryById(
          state.data.categories,
          state.data.categorySelectedId,
        );
      }
    },
    removeChildItem: (state, action: PayloadAction<{ parentId?: string | number | null; childId: string | number }>) => {
      logInfo('Se filtra el child de producto borrado');
      const { parentId, childId } = action.payload;
      let parentIndex = -1;

      if (parentId !== undefined && parentId !== null) {
        parentIndex = state.data.items.findIndex(item => idsMatch(item.id, parentId));
      }

      if (parentIndex === -1) {
        parentIndex = state.data.items.findIndex(item =>
          item.children?.some(child => idsMatch(child.id, childId)) ?? false,
        );
      }

      logInfo('Se filtra el producto borrado - Index encontrado: ', parentIndex);
      if (parentIndex !== -1) {
        const parent = state.data.items[parentIndex];
        if (parent.children) {
          parent.children = parent.children.filter(child => !idsMatch(child.id, childId));

          // Si no quedan hijos, eliminamos el campo children
          if (parent.children.length === 0) {
            delete parent.children;
          }
        }
      }
      state.data.products = state.data.items.filter(i => i.item_type === 'product');
      state.data.services = state.data.items.filter(i => i.item_type === 'service');
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchCfgCatalogueDataAsync.fulfilled, (state, action: PayloadAction<CfgCatalogueData | undefined>) => {
      if (action.payload) {
        state.cfg = action.payload;
        logInfo('--------- fetchCfgCatalogueDataAsync action.payload: ', action.payload);
        // state.isProductLoading = false;
      } else {
        // Maneja adecuadamente el caso cuando el payload es undefined, por ejemplo:
        state.error = "No se pudo cargar la configuración del catálogo.";
      }
    });
    builder.addCase(fetchItemsAsync.fulfilled, (state, action: PayloadAction<CatalogueDataListResponse>) => {
      const items = filterItems(action.payload.results);
      logInfo('fetchItemsAsync.fulfilled action.payload: ', action.payload, ' action.payload.results: ', items);
      state.data.items = items;
      state.data.products = items.filter(i => i.item_type === 'product');
      state.data.services = items.filter(i => i.item_type === 'service');
      state.data.hasMore = action.payload.next !== null;
      state.data.page = 2;

      logInfo('--------- isProductLoading false en: 2');
      state.isItemLoading = false;
      state.hasLoaded = true;
    });
    builder.addCase(fetchAddItemsFromPageAsync.fulfilled, (state, action: PayloadAction<{ page: number, itemsToAdd: Item[], next: string }>) => {
      const newItems = filterItems(action.payload.itemsToAdd);
      state.data.items = [...state.data.items, ...newItems];
      state.data.products = state.data.items.filter(i => i.item_type === 'product');
      state.data.services = state.data.items.filter(i => i.item_type === 'service');
      state.data.hasMore = action.payload.next !== null;
      state.data.page = action.payload.page + 1;

      logInfo('--------- isProductLoading false en: 3');
      state.isItemLoading = false;
    });
    builder.addCase(pollPromotionHydrationAsync.fulfilled, (state, action: PayloadAction<PromotionHydrationRefreshResponse>) => {
      const patches = action.payload.results ?? [];
      if (!patches.length) {
        return;
      }
      state.data.items = mergePromotionHydrationPatchesForItems(state.data.items, patches);
      state.data.products = state.data.items.filter(i => i.item_type === 'product');
      state.data.services = state.data.items.filter(i => i.item_type === 'service');
    });
    builder.addCase(
      fetchCategoriesAsync.fulfilled,
      (
        state,
        action: PayloadAction<
          Category[],
          string,
          { arg: { node_id?: number; depth?: number } | undefined }
        >
      ) => {
        const { node_id } = action.meta.arg || {};
        if (node_id) {
          const target = findCategoryById(state.data.categories, node_id);
          if (target) {
            target.children = action.payload[0]?.children || [];
          }
        } else {
          state.data.categories = action.payload;
        }

        if (state.data.categorySelectedId) {
          state.data.selectedCategory = findCategoryById(
            state.data.categories,
            state.data.categorySelectedId,
          );
        }

        state.isCategoriesLoading = false;
        console.log('Categories: ', state.data.categories);
      },
    );
    builder.addCase(
      fetchFallbackImagesAsync.fulfilled,
      (state, action) => {
        if (action.payload && action.payload.length > 0) {
          action.payload.forEach(({ category_id, image }) => {
             const target = findCategoryById(state.data.categories, category_id);
             if (target) {
                 target.image = image;
             }
          });
          
          if (state.data.categorySelectedId) {
            state.data.selectedCategory = findCategoryById(
              state.data.categories,
              state.data.categorySelectedId,
            );
          }
        }
      }
    );
    builder.addCase(fetchItemsAsync.pending, (state) => {
      state.data.items = [];
      state.data.products = [];
      state.data.services = [];
      logInfo('****LIMPIO LOS ITEMS PRIMERO fetchItemsAsync');
      state.isItemLoading = true;
    });
    builder.addCase(fetchItemsAsync.rejected, (state) => {

      logInfo('--------- isProductLoading false en: 4');
      state.isItemLoading = false;
      state.hasLoaded = true;
    });
    builder.addCase(fetchAddItemsFromPageAsync.pending, (state) => {
      state.isItemLoading = true;
    });
    builder.addCase(fetchAddItemsFromPageAsync.rejected, (state) => {

      logInfo('--------- isProductLoading false en: 5');
      state.isItemLoading = false;
    });
    // builder.addCase(fetchCategories.fulfilled, (state, action: PayloadAction<Category[]>) => {
    //     state.categories = action.payload;
    //     state.loading = false;
    // });
    builder.addCase(fetchCategoriesAsync.pending, (state) => {
      state.isCategoriesLoading = true;
    });
    builder.addCase(fetchCategoriesAsync.rejected, (state) => {
      state.isCategoriesLoading = false;
    });
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    builder.addCase(fetchCatalogueItemUpdateAsync.fulfilled, (state, action: PayloadAction<{ id: number; data: Item }, string, { arg: { id: number; data: { [key: string]: any }; isFullUpdate: boolean } }>) => {
      const { id, data } = action.payload;
      const isFullUpdate = action.meta.arg.isFullUpdate;  // Ahora TypeScript reconoce `meta`

      logInfo('fetchCatalogueProductUpdateAsync.fulfilled - data: ', data);

      const updateItem = (existingItem: Item | Child, newData: Item, isFullUpdate: boolean) => {
        return isFullUpdate
          ? newData // Si es una actualización completa, reemplaza todos los datos
          : { ...existingItem, ...newData }; // Si es parcial, mezcla los campos
      };

      if (data.is_child) {
        const id_to_find = data.parent?.id;
        if (id_to_find !== undefined) {
          const parentIndex = state.data.items.findIndex((item) => item.id === id_to_find);
          if (parentIndex !== -1) {
            const parentProd = state.data.items[parentIndex];
            if (parentProd.children) {
              const index = parentProd.children.findIndex((child) => child.id === id);
              if (index !== -1) {
                // const dataChild = {
                //     id: data.id,
                //     name: data.name,
                //     stock: data.stock,
                //     is_child: true,
                //     is_visible_web: data.is_visible_web,
                //     upc: data.upc,
                //     price: data.price,
                //     extra_info: data.extra_info,
                // };
                // logInfo('fetchCatalogueProductUpdateAsync.fulfilled - dataChild: ', dataChild);
                // parentProd.children[index] = dataChild;
                parentProd.children[index] = updateItem(parentProd.children[index], data, isFullUpdate);
              } else { logError('No se encontró child. Data: ', data, ' id: ', id); }
            } else { logError('El producto padre no tiene hijos. Data: ', data, ' id: ', id); }
          } else { logError('No se encontró el producto padre. Data: ', data, ' id: ', id); }
        } else { logError('El ID del padre es undefined. Data: ', data); }
      } else {
        const index = state.data.items.findIndex((item) => item.id === id);
        if (index !== -1) {
          // state.data.products[index] = filterProduct(data);
          const filteredItemData = filterItem(data);
          state.data.items[index] = updateItem(state.data.items[index], filteredItemData, isFullUpdate);
        } else { logError('No se encontró el producto. Data: ', data, ' id: ', id); }
      }
      logInfo('--------- isProductLoading false en: 6');
      state.isItemLoading = false;
      state.error = null;
    });
    builder.addCase(fetchCatalogueItemUpdateAsync.pending, (state) => {
      state.isItemLoading = true;
      state.error = null;
    });
    builder.addCase(fetchCatalogueItemUpdateAsync.rejected, (state, action) => {
      logInfo('--------- isProductLoading false en: 7');
      state.isItemLoading = false;

      if (action.payload && typeof action.payload === 'object') {
        state.error = action.payload as ErrorState
      } else {
        state.error = { message: 'Error al cargar los datos de producto.' };
      }
    });

    // Bulk toggle handlers
    builder.addCase(fetchBulkToggleVisibilityAsync.pending, (state) => {
      state.isItemLoading = true;
    });
    builder.addCase(
      fetchBulkToggleVisibilityAsync.fulfilled,
      (state, action) => {
        const { summary, toggles } = action.payload;
        const updatedIds = new Set(summary.updated_ids.map((value) => Number(value)));

        if (updatedIds.size > 0) {
          state.data.items = state.data.items.map((item) => {
            if (!updatedIds.has(Number(item.id))) {
              return item;
            }

            const nextItem: Item = { ...item };
            if (toggles.is_visible_web !== undefined) {
              nextItem.is_visible_web = toggles.is_visible_web;
            }
            if (toggles.is_active !== undefined) {
              nextItem.is_active = toggles.is_active;
            }
            return nextItem;
          });
          state.data.products = state.data.items.filter(i => i.item_type === 'product');
          state.data.services = state.data.items.filter(i => i.item_type === 'service');
        }

        state.isItemLoading = false;
      }
    );
    builder.addCase(
      fetchBulkToggleVisibilityAsync.rejected,
      (state, action) => {
        state.isItemLoading = false;
        logError('Error en bulk toggle:', action.payload || action.error);
      }
    );
  }
});

export const {
  setCategorySelected,
  setInitialized,
  setOrderby,
  setSearchPrompt,
  setFilters,
  resetDashboardItemsQueryState,
  setAspectRadio,
  removeItem,
  upsertCategory,
  removeChildItem,
  setItems,
} = eshopSlice.actions;

// Selectors
export const selectItems = (state: RootState) => state?.eshop?.data?.items ?? [];

const selectFiltersState = (state: RootState) => state?.eshop?.data?.filters ?? {};
const selectUserIsStaff = (state: RootState) => Boolean(state?.eshop?.cfg?.user_is_staff);
const selectErpFeatureEnabled = (state: RootState) => selectErpEnabled(state);

const selectIncludeDeleted = createSelector(
  [selectFiltersState],
  (filters) => Boolean(filters.deleted ?? filters.include_deleted),
);

interface VisibilitySelectorOptions {
  erpEnabled: boolean;
  includeInternal: boolean;
  includeUnavailable: boolean;
}

const selectVisibilityOptions = createSelector(
  [selectErpFeatureEnabled, selectIncludeDeleted, selectUserIsStaff],
  (erpEnabled, includeDeleted, isStaff): VisibilitySelectorOptions => ({
    erpEnabled,
    includeInternal: includeDeleted || isStaff,
    includeUnavailable: includeDeleted,
  }),
);

const selectItemsWithVisibility = createSelector(
  [selectItems, selectVisibilityOptions],
  (items, { erpEnabled }) => filterItems(items, { erpEnabled }),
);

export const selectFilteredItems = createSelector(
  [selectItemsWithVisibility, selectVisibilityOptions],
  (items, options) => {
    if (options.includeUnavailable) {
      return items;
    }

    return items.filter((item) => {
      if (item.visibility?.isPublishable) {
        return true;
      }
      return !!(options.includeInternal && item.visibility?.isInternal);

    });
  },
);

export const selectCatalogueRootItems = createSelector(
  [selectFilteredItems],
  (items) =>
    items.filter((item) => {
      if (item.is_child) {
        return false;
      }
      return item.parent_id === null || item.parent_id === undefined;
    }),
);

export const selectInternalItems = createSelector(
  [selectItemsWithVisibility],
  (items) => items.filter((item) => item.visibility?.isInternal),
);


export default eshopSlice.reducer;
